import { observer } from 'mobx-react-lite';
import { ContentBox } from '../../../lib/components/compound/pages/contentBox';
import { ContentHead, useForm } from '../../../lib/components';
import { BenefitBudgetForm } from '../../../pages/actions/updateBudget/form';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../hooks/useStore';
import {
  ApiError,
  EmployeeBenefitBudgetUpdates,
  OperationEnum
} from '@pxtra-client-lib-legacy/gen';
import { useEffect, useState } from 'react';
import Util from '../../../misc/util';
import { Box, Typography } from '@mui/material';
import { Snackbar, TooltipComponent } from '../../../lib/components/atomic/feedback';
import { BudgetIcon, InfoIcon } from '../../../lib/icons';
import { useDate } from '../partials/profilePagesPartial';
import { TableDataGrid } from '../../../lib/components/compound/tableDataGrid';
import { Button } from '../../../lib/components/atomic/button';

export const EmployeeBenefitBudget = observer(() => {
  const { employeeId } = useParams();
  const { employeeStore } = useStore();
  const [updateType, setUpdateType] = useState('FIXED_VALUE');
  const { t } = useTranslation();
  const form = useForm();
  const oldBudget = employeeStore.currentEmployee?.benefit_budget.base_available_amount ?? 0;
  const [newBudget, setNewBudget] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [invalidUpdate, setInvalidUpdate] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [startDate, setDate] = useDate();
  const [rows, setRows] = useState<EmployeeBenefitBudgetUpdates[]>([]);

  const handleTypeChanged = (newType?: string) => {
    newType && setUpdateType(newType.toUpperCase());
  };

  const calcBudget = (value: number) => {
    const l_newBudget = Util.calcBenefitBudget(value, updateType as OperationEnum, {
      benefit_budget: oldBudget
    });
    return l_newBudget;
  };

  const validateBudget = (newBudget: number, date = startDate) => {
    if (newBudget <= 0) {
      return false;
    }
    if (newBudget < oldBudget) {
      setErrorMessage(t('form:BUDGET_UPDATE_ERROR', { context: 'reduction' }));
      return false;
    }
    for (const entry of rows) {
      const updateStart = new Date(entry.start_accounting_period);
      updateStart.setHours(0);
      if (date >= updateStart) {
        if (newBudget < entry.amount) {
          setErrorMessage(t('form:BUDGET_UPDATE_ERROR', { context: 'low' }));
          return false;
        }
      } else if (newBudget > entry.amount) {
        setErrorMessage(t('form:BUDGET_UPDATE_ERROR', { context: 'high' }));
        return false;
      }
    }
    setErrorMessage('');
    return true;
  };

  const fetchBudgetUpdates = async () => {
    try {
      const result = await employeeStore.fetchBudgetUpdates(employeeId || '');
      if (result) {
        const formattedData = result.map((item, index) => ({
          id: index + 1,
          ...item
        }));
        setRows(formattedData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBudgetUpdates();
  }, []);
  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('form:EDIT_BENEFIT_BUDGET')}
          renderActions={() => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <Button onClick={form.handleSubmit} disabled={invalidUpdate}>
                {t('common:SAVE')}
              </Button>
            </Box>
          )}
          showActions={true}
        />
      }
    >
      <div
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <BenefitBudgetForm
          formRef={form.formRef}
          selectedEmployeeIds={employeeId ? [employeeId] : []}
          onValueChanged={(value) => {
            const newBudget = calcBudget(typeof value === 'number' ? value : 0);
            setNewBudget(newBudget);
            setInvalidUpdate(!validateBudget(newBudget, startDate));
          }}
          onDateChanged={(date) => {
            if (!date) return;
            setDate(new Date(date));
            setInvalidUpdate(!validateBudget(newBudget, new Date(date)));
          }}
          onTypeChanged={handleTypeChanged}
          onSubmitFailed={(error) => {
            if (error instanceof ApiError) {
              let message = '';
              if ('body' in error && error.body) {
                message =
                  error.body.errors?.non_field_errors?.[0] ??
                  error.body.non_field_errors?.[0] ??
                  error.body.message ??
                  '';
              }
              setErrorMessage(message);
              setInvalidUpdate(true);
            }
          }}
          additionalSubmit={() => {
            setOpenSnackbar(true);
            form.formRef.current?.resetForm({ values: form.formRef.current.values });
            fetchBudgetUpdates();
            setTimeout(() => {
              setOpenSnackbar(false);
            }, 3000);
          }}
          useCustomButton
        />
      </div>
      <Box sx={{ display: 'flex' }}>
        <TooltipComponent title={t('form:BUDGET_UPDATE_TOOLTIP')} placement="right" arrow>
          <Box paddingLeft="8px" paddingTop="0.7rem" paddingRight="1rem" maxWidth="fit-content">
            <InfoIcon
              height={28}
              width={18}
              color={invalidUpdate ? 'error.main' : 'text.secondary'}
            />
          </Box>
        </TooltipComponent>
        <Typography
          variant="body2"
          paddingTop="1rem"
          color={invalidUpdate ? 'error.main' : 'text.secondary'}
        >
          {t('form:NEW_BUDGET')}: {Util.formatCurrency(newBudget)} {errorMessage}
        </Typography>
      </Box>
      {rows.length > 0 && (
        <Box>
          <Typography variant="h3" marginTop="50px" marginBottom="15px">
            {t('form:UPCOMING_UPDATES')}
          </Typography>
          <TableDataGrid
            columns={[
              {
                field: 'start_accounting_period',
                headerName: t('form:START_DATE'),
                type: 'date',
                flex: 3,
                valueGetter: ({ value }: { value: string }) => Util.formatDate(value)
              },
              {
                field: 'amount',
                headerName: t('form:AMOUNT'),
                flex: 3,
                valueGetter: ({ value }: { value: number }) => Util.formatCurrency(value)
              }
            ]}
            rows={rows}
            checkboxSelection={false}
            autoHeight={rows.length > 0}
            hideFooter
            hideToolbar
          />
        </Box>
      )}
      <Snackbar
        text={t('form:BENEFIT_BUDGET_UPDATE_MESSAGE')}
        icon={<BudgetIcon color="success.main" />}
        open={openSnackbar}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom'
        }}
      />
    </ContentBox>
  );
});
